import { useEffect, useId, useRef, useState } from 'react';
import { IconAutoScroll } from '@/components/home/icons';
import type { AutoScrollSpeed } from '@/hooks/useAutoScroll';

/**
 * Auto-scroll dropdown for the Surah reader.
 *
 * Renders inline as a pill — designed to live on its own centered row
 * beneath the EN/UR/BOTH/TR + TAFSIR pill cluster inside SettingsBar.
 * The pill matches the toolbar pill idiom (rounded, dark surface,
 * hairline border, beam-tinted when active).
 *
 * The trigger uses `relative` positioning so its dropdown menu pops
 * out directly above/below it — no fixed positioning, no z-index
 * games — keeping focus + click-outside behavior simple.
 *
 * Dropdown UX:
 *   - Click trigger to open
 *   - Click outside / Escape / select option → close
 *   - role=menu + role=menuitemradio + aria-checked for screen readers
 *   - Trigger announces active speed via aria-label
 */

const OPTIONS: { value: AutoScrollSpeed; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: '0.5x', label: '½× — very slow' },
  { value: '1x', label: '1× — slow' },
  { value: '2x', label: '2× — medium' },
  { value: '3x', label: '3× — fast' },
];

type Props = {
  speed: AutoScrollSpeed;
  onSpeedChange: (next: AutoScrollSpeed) => void;
};

export function AutoScrollControl({ speed, onSpeedChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  const isActive = speed !== 'off';
  const activeLabel =
    speed === 'off' ? 'Auto scroll' : `Auto scroll, ${speed} active`;
  const badge = speed === 'off' ? null : speed;

  // Close on Escape + click-outside. Single effect for both so we
  // can hand the same wrapper ref to both checks.
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        // Return focus to the trigger so keyboard users aren't stranded.
        triggerRef.current?.focus();
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  function handleSelect(next: AutoScrollSpeed) {
    onSpeedChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  }

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={activeLabel}
        title={activeLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        className={[
          'inline-flex items-center gap-2 rounded-full px-4 py-2',
          'text-xs font-semibold uppercase tracking-wide',
          'border transition-colors duration-150',
          isActive
            ? 'border-beam/60 bg-beam/15 text-beam'
            : 'border-night-500/40 bg-night-800 text-night-200 hover:border-beam/50 hover:text-beam-pale',
        ].join(' ')}
      >
        <IconAutoScroll className="h-4 w-4" />
        <span>{isActive ? `Auto · ${badge}` : 'Auto scroll'}</span>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Auto scroll speed"
          className="absolute left-1/2 top-[calc(100%+0.5rem)] z-50 min-w-[12rem] -translate-x-1/2 rounded-xl border border-night-500/40 bg-night-800/95 p-1.5 shadow-2xl backdrop-blur-md"
        >
          {OPTIONS.map((opt) => {
            const selected = opt.value === speed;
            return (
              <button
                key={opt.value}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => handleSelect(opt.value)}
                className={[
                  'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                  selected
                    ? 'bg-beam/15 text-beam'
                    : 'text-night-100 hover:bg-night-700',
                ].join(' ')}
              >
                <span>{opt.label}</span>
                {selected && (
                  <span aria-hidden="true" className="text-xs">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
